import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/clients/supabase';
import { useToast } from '@/shared/contexts/ToastContext';

/**
 * Auth Confirm Handler - Handles token exchange for password reset
 * This component processes the token from email links and exchanges it for a valid session
 * WITHOUT requiring the user to be logged in first
 */
const AuthConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleTokenExchange = async () => {
      try {
        // Enhanced logging for debugging
        console.log('🔍 AuthConfirm - Full URL:', window.location.href);
        console.log('🔍 AuthConfirm - Pathname:', window.location.pathname);
        console.log('🔍 AuthConfirm - Search:', window.location.search);
        console.log('🔍 AuthConfirm - Hash:', window.location.hash);
        
        // Check for URL fragments first (Supabase sends tokens in hash)
        const urlFragment = window.location.hash;
        
        // Handle success case - Supabase sends tokens in URL fragment
        if (urlFragment.includes('access_token=') && urlFragment.includes('type=recovery')) {
          console.log('✅ Recovery session detected in URL fragment');
          
          // Get email from token if available
          try {
            const fragmentParams = new URLSearchParams(urlFragment.substring(1));
            const accessToken = fragmentParams.get('access_token');
            if (accessToken) {
              // Decode JWT to get email (basic decode, no verification needed here)
              const payload = JSON.parse(atob(accessToken.split('.')[1]));
              if (payload.email) {
                localStorage.setItem('reset_password_email', payload.email);
                localStorage.setItem('reset_password_timestamp', Date.now().toString());
              }
            }
          } catch (e) {
            console.log('Could not extract email from token, but that\'s okay');
          }
          
          // ✅ PERBAIKAN: Tunggu session terbentuk sebelum redirect
          let retryCount = 0;
          const maxRetries = 20; // 10 detik total (500ms x 20)
          
          const waitForSession = async () => {
            try {
              const { data: { session }, error } = await supabase.auth.getSession();
              
              if (error) {
                console.error('❌ Error getting session:', error);
                retryCount = maxRetries; // Stop retrying on error
              }
              
              if (session && session.user) {
                console.log('✅ Session established for user:', session.user.email);
                showToast({
                  type: 'success',
                  message: 'Link berhasil diverifikasi. Silakan buat password baru.'
                });
                navigate('/reset-password');
                return;
              }
              
              retryCount++;
              if (retryCount < maxRetries) {
                console.log(`⏳ Waiting for session... (${retryCount}/${maxRetries})`);
                setTimeout(waitForSession, 500); // Retry setiap 500ms
              } else {
                console.error('❌ Session not established after maximum retries');
                showToast({
                  type: 'error',
                  message: 'Gagal membuat sesi reset. Link mungkin bermasalah, silakan coba lagi.'
                });
                navigate('/forgot-password');
              }
            } catch (error) {
              console.error('❌ Exception while waiting for session:', error);
              showToast({
                type: 'error',
                message: 'Terjadi kesalahan saat memproses link reset.'
              });
              navigate('/forgot-password');
            }
          };
          
          // Mulai tunggu session setelah memberi waktu untuk URL fragment processing
          setTimeout(waitForSession, 200);
          return;
        }
        
        // Handle error case
        if (urlFragment.includes('error=')) {
          const fragmentParams = new URLSearchParams(urlFragment.substring(1));
          const error = fragmentParams.get('error');
          const errorCode = fragmentParams.get('error_code');
          const errorDescription = fragmentParams.get('error_description');

          console.log('❌ Auth error detected:', { error, errorCode, errorDescription });

          // Handle specific error cases
          if (errorCode === 'otp_expired' || errorDescription?.includes('expired')) {
            showToast({
              type: 'error',
              message: 'Link reset password sudah kadaluarsa. Silakan minta link baru.',
              duration: 6000
            });
          } else if (error === 'access_denied') {
            showToast({
              type: 'error',
              message: 'Link reset password tidak valid atau sudah digunakan. Kemungkinan link sudah diklik sebelumnya atau email scanner mengakses link. Silakan minta link baru.',
              duration: 8000
            });
          } else {
            showToast({
              type: 'error',
              message: 'Link reset password bermasalah. Silakan minta link baru.',
              duration: 6000
            });
          }
          
          navigate('/forgot-password');
          return;
        }

        // ✅ PERBAIKAN: Validasi URL fragment lebih ketat
        if (urlFragment && !urlFragment.includes('access_token=')) {
          console.warn('⚠️ URL fragment exists but no access_token found:', urlFragment);
          showToast({
            type: 'error',
            message: 'Link reset password tidak lengkap. Silakan minta link baru.',
            duration: 5000
          });
          navigate('/forgot-password');
          return;
        }

        // Get parameters from URL - Supabase sends different formats
        let token_hash = searchParams.get('token_hash') || searchParams.get('token');
        let type = searchParams.get('type') || 'recovery';
        const next = searchParams.get('next') || '/reset-password';

        console.log('🔍 URL Parameters received:', {
          token_hash: token_hash ? token_hash.substring(0, 10) + '...' : 'null',
          type,
          next,
          fullUrl: window.location.href,
          allParams: Object.fromEntries(searchParams)
        });

        if (!token_hash || !type) {
          console.error('❌ Missing token parameters:', { 
            token_hash, 
            type, 
            searchParams: Object.fromEntries(searchParams),
            fullUrl: window.location.href,
            referrer: document.referrer
          });
          
          // Enhanced error message with alternatives
          showToast({
            type: 'error',
            message: 'Link tidak valid atau sudah kadaluarsa. Coba metode alternatif.',
            duration: 5000
          });
          
          // Redirect to forgot password with error context
          setTimeout(() => {
            navigate('/forgot-password?error=invalid_link');
          }, 2000);
          return;
        }

        console.log('🔑 Processing token:', { 
          type, 
          token_hash: token_hash ? token_hash.substring(0, 10) + '...' : 'null',
          next,
          allParams: Object.fromEntries(searchParams)
        });

        // Exchange token for session - this is the key part
        // This creates a valid session WITHOUT requiring login
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash,
          type: type as any, // 'recovery' for password reset
        });

        if (error) {
          console.error('Token verification error:', error);
          
          // Enhanced error handling with specific messages
          let errorMessage = 'Gagal memverifikasi link reset. Silakan coba lagi.';
          
          if (error.message.includes('expired') || error.message.includes('Token has expired')) {
            errorMessage = 'Link reset password sudah kadaluarsa. Silakan minta link baru.';
          } else if (error.message.includes('invalid') || error.message.includes('Invalid token')) {
            errorMessage = 'Link reset password tidak valid atau sudah digunakan. Silakan minta link baru.';
          } else if (error.message.includes('not found')) {
            errorMessage = 'Token tidak ditemukan. Link mungkin sudah digunakan atau kadaluarsa.';
          } else if (error.message.includes('rate limit')) {
            errorMessage = 'Terlalu banyak percobaan. Silakan tunggu beberapa menit sebelum mencoba lagi.';
          }
          
          showToast({
            type: 'error',
            message: errorMessage
          });
          
          // Always redirect to forgot password for any token error
          setTimeout(() => {
            navigate('/forgot-password');
          }, 2000); // Give user time to read the message
          return;
        }

        // Success - token exchanged for valid session
        if (data.session && data.user) {
          console.log('Token exchange successful for user:', data.user.email);
          
          // Store email for the reset form
          if (data.user.email) {
            localStorage.setItem('reset_password_email', data.user.email);
            localStorage.setItem('reset_password_timestamp', Date.now().toString());
          }

          showToast({
            type: 'success',
            message: 'Link berhasil diverifikasi. Silakan buat password baru.'
          });

          // Redirect to reset password page with email parameter
          const email = data.user.email;
          const redirectUrl = `${next}${email ? `?email=${encodeURIComponent(email)}` : ''}`;
          console.log('✅ AuthConfirm - Redirecting to:', redirectUrl);
          navigate(redirectUrl);
        } else {
          showToast({
            type: 'error',
            message: 'Gagal membuat sesi reset. Silakan coba lagi.'
          });
          navigate('/forgot-password');
        }

      } catch (error) {
        console.error('Auth confirm error:', error);
        showToast({
          type: 'error',
          message: 'Terjadi kesalahan saat memproses link reset.'
        });
        navigate('/forgot-password');
      } finally {
        setIsProcessing(false);
      }
    };

    handleTokenExchange();
  }, [searchParams, navigate, showToast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full">
        <div className="card p-8 text-center">
          {isProcessing ? (
            <>
              {/* Processing State */}
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Memverifikasi Link Reset
              </h2>
              <p className="text-gray-600">
                Mohon tunggu sebentar...
              </p>
            </>
          ) : (
            <>
              {/* Error State */}
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Link Reset Bermasalah
              </h2>
              <div className="text-sm text-gray-600 mb-6 space-y-3">
                <p>
                  Link reset password tidak dapat digunakan. Kemungkinan penyebab:
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Link sudah kadaluarsa (lebih dari 1 jam)</li>
                  <li>Link sudah pernah diklik sebelumnya</li>
                  <li>Email scanner otomatis mengakses link</li>
                  <li>Link rusak atau tidak lengkap</li>
                </ul>
                <p className="text-xs text-gray-500 mt-3">
                  💡 <strong>Tips:</strong> Jangan klik link reset dari preview email. Buka email secara penuh terlebih dahulu.
                </p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="btn-primary w-full"
                >
                  Minta Link Reset Baru
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="btn-secondary w-full"
                >
                  Kembali ke Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthConfirm;